import { PlusOutlined, SearchOutlined } from "@ant-design/icons"
import { Alert, Button, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { DebouncedInput } from "../../components/Input/DebouncedInput.tsx"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { isMeetingAutomation } from "../Communication/automations/meeting-automations.ts"
import { useSurfacePreference } from "../../hooks/use-surface-preference.ts"
import { AutomationSubTabs } from "../Settings/components/common/AutomationSubTabs.tsx"
import { surfaceKey } from "../User/redux/ui-preferences.types.ts"
import { getAutomations } from "./api/services.ts"
import { AutomationSurface } from "./components/AutomationSurface"
import { AutomationsTable } from "./components/AutomationsTable"
import { HistoryDrawer } from "./components/HistoryDrawer"
import {
  NoAutomations,
  NoMatches,
  OverviewLoadError,
  OverviewLoading
} from "./components/OverviewStates"
import {
  type OverviewSort,
  matchesQuery,
  readOverviewSort
} from "./helpers/automation-list"
import { useAutomationRowActions } from "./hooks/use-automation-row-actions"
import type { Automation } from "./types/automation.types"

const { Text } = Typography

// Search runs client-side, so the whole list is fetched, a page at a time.
const LIST_PAGE_SIZE = 1000
const MAX_PAGES = 50
const QUERY_PARAM = "q"
const SORT_KEY = surfaceKey("settings.automation.overview-sort")

export default function AutomationOverviewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canManage = useAppSelector(
    (state) =>
      !!state.auth.permissions?.codes?.settings?.automations
        ?.settings_automation_manage
  )

  const [storedSort, storeSort] = useSurfacePreference<OverviewSort | null>(
    SORT_KEY,
    null
  )
  const sort = readOverviewSort(storedSort)

  const [rows, setRows] = useState<Automation[]>([])
  const [meetingCount, setMeetingCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [historyFor, setHistoryFor] = useState<Automation | null>(null)
  const { togglingIds, toggle, duplicate, remove, announcement } =
    useAutomationRowActions(setRows)

  const query = searchParams.get(QUERY_PARAM) ?? ""
  const setQuery = (next: string) =>
    setSearchParams(
      (prev) => {
        if (next) prev.set(QUERY_PARAM, next)
        else prev.delete(QUERY_PARAM)
        return prev
      },
      { replace: true }
    )

  const load = useCallback(async () => {
    setLoading(true)
    setLoadFailed(false)
    try {
      // Follows `next` so search still sees every automation past one page,
      // stopping once the reported count is held or after MAX_PAGES, so a
      // server that keeps answering `next` cannot loop the page forever.
      const all: Automation[] = []
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const response = await getAutomations({
          page,
          page_size: LIST_PAGE_SIZE
        })
        if (!response.ok) {
          setLoadFailed(true)
          return
        }
        all.push(...(response.data?.results ?? []))
        const count = response.data?.count
        if (
          !response.data?.next ||
          (typeof count === "number" && all.length >= count)
        )
          break
      }
      setRows(all.filter((automation) => !isMeetingAutomation(automation)))
      setMeetingCount(all.filter(isMeetingAutomation).length)
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const visibleRows = useMemo(
    () => rows.filter((row) => matchesQuery(t, row, query)),
    [rows, query, t]
  )

  // The menu that opened History is gone by the time the drawer closes, so
  // focus goes back to that row's actions button rather than the page top.
  const historyRowRef = useRef<string | null>(null)
  const openHistory = (automation: Automation) => {
    historyRowRef.current = automation.id
    setHistoryFor(automation)
  }
  const returnFocusToRow = () => {
    const id = historyRowRef.current
    historyRowRef.current = null
    if (!id) return
    document
      .querySelector<HTMLElement>(
        `[data-testid='automation-overview'] tr[data-row-key="${CSS.escape(id)}"] button[aria-label]`
      )
      ?.focus()
  }

  const openNew = () => navigate(RoutesPaths.settings.automationNew)
  const openBuilder = (automation: Automation) =>
    navigate(RoutesPaths.settings.automationEdit(automation.id))

  const renderList = () => {
    if (loadFailed) return <OverviewLoadError onRetry={() => void load()} />
    if (loading) return <OverviewLoading />
    if (!rows.length)
      return <NoAutomations onCreate={canManage ? openNew : undefined} />
    if (!visibleRows.length)
      return <NoMatches query={query} onClear={() => setQuery("")} />
    return (
      <AutomationsTable
        rows={visibleRows}
        canManage={canManage}
        togglingIds={togglingIds}
        onEdit={openBuilder}
        onDuplicate={(automation) => void duplicate(automation)}
        onToggle={(automation, next) => void toggle(automation, next)}
        onDelete={(automation) => void remove(automation)}
        onHistory={openHistory}
        sort={sort}
        onSortChange={storeSort}
      />
    )
  }

  return (
    <div className={"flex w-full min-w-0 flex-1 flex-col"}>
      <AutomationSubTabs />
      <AutomationSurface
        data-testid={"automation-overview"}
        className={"flex flex-col gap-4"}
      >
        <div className={"flex flex-wrap items-start justify-between gap-2"}>
          <div>
            {/* The global h1-h3 rule centres headings. */}
            <h2 className={"text-start text-lg font-semibold"}>
              {t("settingsAutomation.overview.title")}
            </h2>
            <p className={"text-gray-500"}>
              {t("settingsAutomation.overview.subtitle")}
            </p>
          </div>
          {canManage && (
            <Button
              type={"primary"}
              icon={<PlusOutlined />}
              className={"max-sm:h-11"}
              onClick={openNew}
            >
              {t("settingsAutomation.overview.newAutomation")}
            </Button>
          )}
        </div>

        {meetingCount > 0 && (
          <Alert
            type={"info"}
            showIcon
            message={
              <span>
                {t("settingsAutomation.overview.meetingAutomationsMoved")}{" "}
                <Link to={RoutesPaths.communication.automations}>
                  {t(
                    "settingsAutomation.overview.openCommunicationAutomations"
                  )}
                </Link>
              </span>
            }
          />
        )}

        <section
          aria-labelledby={"your-automations-heading"}
          className={
            "flex min-w-0 flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4"
          }
        >
          <div className={"flex flex-wrap items-center justify-between gap-2"}>
            <div className={"flex items-baseline gap-2"}>
              <h3
                id={"your-automations-heading"}
                className={"m-0 text-start text-base font-semibold"}
              >
                {t("settingsAutomation.overview.listHeading")}
              </h3>
              {!loading && !loadFailed && (
                <Text type={"secondary"}>
                  {query.trim() && rows.length
                    ? t("settingsAutomation.overview.filteredCount", {
                        visible: visibleRows.length,
                        count: rows.length
                      })
                    : t("settingsAutomation.overview.count", {
                        count: rows.length
                      })}
                </Text>
              )}
            </div>
            <DebouncedInput
              aria-label={t("settingsAutomation.overview.searchLabel")}
              placeholder={t("settingsAutomation.overview.searchPlaceholder")}
              prefix={<SearchOutlined />}
              value={query}
              onValueChange={(next) => setQuery(next ?? "")}
              className={"w-full sm:w-80"}
            />
          </div>
          {renderList()}
        </section>

        {/* Row outcomes (pause, enable, duplicate, delete) for screen readers;
          the toasts are not announced. */}
        <div
          role={"status"}
          aria-live={"polite"}
          className={"sr-only"}
          data-testid={"automation-announcer"}
        >
          <span key={announcement.id}>{announcement.text}</span>
        </div>

        <HistoryDrawer
          automation={historyFor}
          onClose={() => setHistoryFor(null)}
          afterClose={returnFocusToRow}
        />
      </AutomationSurface>
    </div>
  )
}
